import React, { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { getOperationDownloadURL } from "../../../../Actions/API/Job/JobDownloadActions"
import { sleep } from "../../../../Helpers/Functions/time"
import { useBoolean } from "../../../../Hooks/useBoolean"
import {
    APIAppAsyncOperationModel,
    getAsyncOperationDisplayText
} from "../../../../Models/API/APIAppAsyncOperationModel"
import { MainThunkDispatch } from "../../../../Reducers"
import { Banner } from "../../../Common/Banner/Banner"

interface AsyncOperationStatusBannerProps {
    className?: string
    model: APIAppAsyncOperationModel
    onOperationRetry: (operationID: number) => Promise<void>
    onOperationArchive: (operationID: number) => Promise<void>
    onDownload: (downloadURL: string) => void
}

export const AsyncOperationStatusBanner: FC<AsyncOperationStatusBannerProps> =
    ({
        className,
        model,
        onOperationRetry,
        onOperationArchive,
        onDownload
    }) => {
        const [isRetrying, setRetryingTrue, setRetryingFalse] =
            useBoolean(false)
        const [isArchiving, setArchivingTrue, setArchivingFalse] =
            useBoolean(false)
        const handleRetry = useCallback(() => {
            setRetryingTrue()
            onOperationRetry(model.id).finally(setRetryingFalse)
        }, [setRetryingFalse, setRetryingTrue, onOperationRetry, model.id])

        const handleArchive = useCallback(() => {
            setArchivingTrue()
            onOperationArchive(model.id).finally(setArchivingFalse)
        }, [setArchivingFalse, setArchivingTrue, onOperationArchive, model.id])

        const [downloadURL, setDownloadURL] =
            useState<string | undefined>(undefined)

        const handleDownload = useCallback(() => {
            if (downloadURL) onDownload(downloadURL)
        }, [downloadURL, onDownload])

        const dispatch = useDispatch<MainThunkDispatch>()
        useEffect(() => {
            let retry = 0
            const fetchDownloadURL = (retry: number) => {
                dispatch(getOperationDownloadURL(model.appUuid, model.id))
                    .then(setDownloadURL)
                    .catch(async error => {
                        console.log(error)
                        // Sometimes it can happen that the operation is completed but the download url endpoint is giving an error, let's retry
                        await sleep(1000)
                        const newRetry = (retry += 1)
                        if (retry < 3) fetchDownloadURL(newRetry)
                    })
            }

            if (model.type === "DOWNLOAD" && model.completed) {
                fetchDownloadURL(retry)
            }
        }, [dispatch, model.appUuid, model.completed, model.id, model.type])

        const text = useMemo<string | undefined>(() => {
            if (model.status === "FAILED" && model.type === "POST_PROCESS") {
                return "Failed to process output of data point"
            }

            if (model.type === "DOWNLOAD") {
                if (model.status === "IN_PROGRESS") {
                    return "Generating data points"
                }

                if (model.status === "COMPLETED") {
                    return "Your data points are ready to download"
                }
            }

            switch (model.type) {
                case "APP_UPDATED":
                    return "Updating data points after settings change"
                case "CANCEL":
                    return "Canceling data points"
                case "DELETE":
                    return "Deleting data points"
                case "RELABEL":
                    return "Sending data points for reprocessing"
                case "START":
                    return "Enqueuing data points for processing"
                case "POST_PROCESS":
                    return "Waiting for processed output"
            }
        }, [model.type, model.status])

        const actions = useMemo(() => {
            if (model.status === "FAILED") {
                return [
                    {
                        label: isRetrying ? "Retrying" : "Retry",
                        onClick: handleRetry,
                        isLoading: isRetrying
                    },

                    {
                        label: isArchiving ? "Archiving" : "Archive",
                        onClick: handleArchive,
                        isLoading: isArchiving
                    }
                ]
            } else if (model.type === "DOWNLOAD") {
                return [
                    {
                        label: "Download",
                        onClick: handleDownload,
                        isLoading: !downloadURL
                    }
                ]
            }

            return undefined
        }, [
            model.status,
            model.type,
            isRetrying,
            handleRetry,
            isArchiving,
            handleArchive,
            handleDownload,
            downloadURL
        ])

        return text === undefined ? null : (
            <Banner
                icon="info"
                className={`flex items-center ${className ?? ""}`}
                actions={actions}
            >
                {getAsyncOperationDisplayText(text, model)}
            </Banner>
        )
    }
